<template>
  <PForm
    nested
    :schema="r$"
    :validate-on="['blur', 'input']"
    class="model-edit-form-rental-dispense-item flex flex-col"
  >
    <div class="primary-lighten-1 flex w-full flex-col gap-y-2 rounded-md p-2">
      <div>
        <PFormField sr-only label="Artikelnaam" name="name">
          <PInput v-model="rentalDispenseItem.name" ref="inputName" placeholder="Artikelnaam" />
        </PFormField>
      </div>
      <div>
        <PFormField sr-only label="Eenheid" name="unit">
          <PInputMenuUnit
            v-model="rentalDispenseItem.unit"
            placeholder="Eenheid"
            :disabled="true"
          />
        </PFormField>
      </div>

      <ModelEditFormProjectRentalDispenseItem
        v-if="creationParameters?.project"
        :project="creationParameters.project"
        :rental-dispense-item="rentalDispenseItem"
        ref="modelEditFormProjectRentalDispenseItem"
      />

      <ModelEditFormKnownBusinessEntityRentalDispenseItem
        v-if="creationParameters?.project"
        :project="creationParameters.project"
        :rental-dispense-item="rentalDispenseItem"
        ref="modelEditFormKnownBusinessEntityRentalDispenseItem"
      />

      <div>
        <PFormField label="Eenheidsprijs per dag">
          <PInputNumber
            v-model="rentalDispenseItem.unitPricePerDay"
            placeholder="Eenheidsprijs per dag"
            allow-zero
          >
            <span class="sm:text-sm">&euro;</span>
          </PInputNumber>
        </PFormField>
      </div>
    </div>
    <PAlertFormValidation :model-value="r$" />
  </PForm>
</template>

<script setup lang="ts">
import PForm from "@/components/form/PForm.vue"
import PAlertFormValidation from "@/components/form/PAlertFormValidation.vue"
import { computed, watchEffect, inject, onMounted, ref } from "vue"
import { useRegle } from "@regle/core"
import PFormField from "@/components/form/PFormField.vue"
import { RentalDispenseItem } from "@/models/rental-dispense-item.model.js"
import { requiredValidator } from "@/helper/validation/validators.js"
import PInput from "@/components/form/PInput.vue"
import PInputNumber from "@/components/form/PInputNumber.vue"
import { upperFirst } from "lodash-es"
import type { RentalDispenseItemController } from "@/controllers/rental-dispense-item-controller.ts"
import { useFormConfigurator } from "@/composables/form/form-configurator"
import { formFocusHack } from "@/helper/form-helper"
import PInputMenuUnit from "@/components/form/PInputMenuUnit.vue"
import { Units } from "@/enums/units.enum"
import { Project } from "@/models/project.model"
import ModelEditFormProjectRentalDispenseItem from "@/components/model_edit_form/ModelEditFormProjectRentalDispenseItem.vue"
import ModelEditFormKnownBusinessEntityRentalDispenseItem from "@/components/model_edit_form/ModelEditFormKnownBusinessEntityRentalDispenseItem.vue"
import { uniqueRentalDispenseItemValidator } from "@/helper/validation/rental-dispense-item-validation.ts"

const inputName = ref()

const controller: RentalDispenseItemController = inject("rentalDispenseItemController")!

const {
  model: rentalDispenseItem,
  initialText,
  isFormInvalid
} = useFormConfigurator<RentalDispenseItem>({
  controller,
  customSaveHandler
})

const creationParameters: { project?: Project } = inject("creationParameters")!

onMounted(() => {
  if (initialText) {
    rentalDispenseItem.value.name = upperFirst(initialText)
  }
  formFocusHack(inputName, rentalDispenseItem, "name")

  rentalDispenseItem.value.unit = Units.PIECE
})

const modelEditFormProjectRentalDispenseItem = ref()
const modelEditFormKnownBusinessEntityRentalDispenseItem = ref()
async function customSaveHandler(save: () => Promise<void>) {
  await save()
  if (creationParameters.project) {
    await modelEditFormProjectRentalDispenseItem.value.savePriceAgreement()
    await modelEditFormKnownBusinessEntityRentalDispenseItem.value.savePriceAgreement()
  }
}
const rules = {
  name: { requiredValidator },
  unit: { requiredValidator },
  $self: { unique: uniqueRentalDispenseItemValidator(rentalDispenseItem) }
}

const { r$ } = useRegle(
  computed(() => ({ name: rentalDispenseItem.value.name, unit: rentalDispenseItem.value.unit })),
  rules,
  { autoDirty: false }
)

watchEffect(() => {
  isFormInvalid.value = r$.$invalid
})
</script>

<style scoped></style>
